import React, { Component } from 'react'
import GoogleMap from 'google-map-react'
import { Anchor, Box, Heading, Main, Paragraph, RadioButtonGroup, Stack, Text } from 'grommet'
import { departmentColors, destinationColors, meshTheme } from '../libs/theme'
import withOpsApi from './withOpsApi'
import { CircleInformation, FormUp, Group, Link } from 'grommet-icons'
import { RoundIconButton } from './Common'
import { PageCard } from './Cards'
import { MotionLayer } from './Motion'
import { AnimatePresence } from 'framer-motion'
import { MeshPin } from '../MeshIcon'
import { FormDown } from 'grommet-icons/es6'
import {
  calculateImpactMetrics,
  getTotalByKeyFromResults,
  getTotalWeightFromResults,
  sentenceCase
} from '../libs/common'
import { opsAPI } from '../api/retail'
import { AppContext } from '../libs/context'
import { DonationMetricsSmall, MetricsBox } from './Donations'
import firebase from '../libs/firebase'
const K_SIZE = 48
const colorByType = {
  'Corporate Customer': departmentColors.deli,
  Farmer: meshTheme.global.colors['mesh-yellow-1'],
  Charity: destinationColors.charity,
  Store: departmentColors.other,
  Government: meshTheme.global.colors['mesh-black']
}
const replacedNames = { 'Restaurant/ Hotel': 'Restaurant Hotel' }
const markerStyle = {
  // initially any map object has left top corner at lat lng coordinates
  // it's on you to set object origin to 0,0 coordinates
  position: 'absolute',
  width: K_SIZE,
  height: K_SIZE,
  left: -K_SIZE / 2,
  top: -K_SIZE / 2,
  cursor: 'pointer'
}
const AttributesAsTags = ({ attributes = {}, hide = [] }) => {
  const allowedAttributes = Object.keys(attributes).map((key) => {
    return hide.includes(key) ? null : key
  })
  return (
    <Box flex={false} direction={'row'} wrap={true}>
      {allowedAttributes.map(
        (attribute) =>
          attribute && (
            <Box
              align={'center'}
              flex={false}
              pad={{ horizontal: 'xsmall', vertical: 'xxsmall' }}
              background={'light-4'}
              key={attribute}
              margin={{ right: 'xsmall', bottom: 'xsmall' }}
              round={'xsmall'}
            >
              <Text color={'dark-6'} size={'xsmall'} weight={'bold'}>
                {attribute}
              </Text>
            </Box>
          )
      )}
    </Box>
  )
}
const AccountCard = ({ name, address, sf_type, sf_attributes = [], data = {}, ...rest }) => {
  const { website } = data
  return (
    <PageCard
      background="white"
      elevation="large"
      heading={
        <Box align="center" gap={'small'} direction={'row'} fill={'horizontal'}>
          <MeshPin size="16px" color={{ light: 'dark-1', dark: 'white' }} />
          <Box flex={'grow'}>
            <Text weight={'bold'}>{name}</Text>
          </Box>
        </Box>
      }
      {...rest}
    >
      <Box pad={{ horizontal: 'medium', vertical: 'small' }} gap={'small'} fill>
        <Box direction={'row'} align={'center'} justify={'start'} gap={'small'}>
          <Paragraph fill>{address}</Paragraph>
          {website && (
            <RoundIconButton
              background={'light-1'}
              Icon={Link}
              round={'small'}
              href={data.website}
              onClick={() => { }}
            />
          )}
        </Box>
        <AttributesAsTags attributes={sf_attributes} hide={['Marketplace', 'Wholesale']} />
        {/*<Text size={'xsmall'} color={'light-6'}>*/}
        {/*  {sf_type}*/}
        {/*</Text>*/}
      </Box>
    </PageCard>
  )
}
const NumberBubble = ({ number = -1, color }) => (
  <Box
    round
    align={'center'}
    justify={'center'}
    height={'tiny'}
    width={'tiny'}
    flex={false}
    background={color || 'light-4'}
  >
    <Text size="xsmall" weight={'bold'}>
      {number}
    </Text>
  </Box>
)
const NetworkSubCategory = ({ name, number, indent, color = 'transparent', reformat = true }) => {
  // hides any underscores used for ordering
  const splitName = name.split('_')
  const finalName = splitName[splitName.length - 1]
  return (
    <Box direction="row" pad={indent && { left: indent }} justify="between" fill={'horizontal'}>
      <Text weight={'bold'} color={indent ? 'dark-6' : 'dark-2'} size="small" key={name}>
        {reformat ? sentenceCase(finalName) : finalName}
      </Text>
      <NumberBubble number={number} color={color} />
    </Box>
  )
}
const NetworkCategory = ({ id, networkStats = {} }) => {
  const total = networkStats[id] ? networkStats[id]['_indicies'].length : -1
  return (
    <Box fill={'horizontal'}>
      <Box
        fill={'horizontal'}
        gap="medium"
        justify="between"
        align={'start'}
        flex="grow"
        direction={'row'}
        key={id}
      >
        <Text weight="bold" color={'mesh-accent-dark'}>
          {sentenceCase(id)}
        </Text>
        <NumberBubble number={total} color={'mesh-green-3'} />
      </Box>
      <Box gap={'xsmall'} pad={{ vertical: 'xsmall' }}>
        {Object.keys(networkStats[id])
          .sort()
          .map((subKey) =>
            subKey.startsWith('_') ? null : (
              <Box key={subKey}>
                <NetworkSubCategory
                  name={subKey}
                  color={'light-4'}
                  number={networkStats[id][subKey]['_indicies'].length}
                />
                {/*hide buyers attrs-----*/}
                {subKey !== 'buyers' &&
                  Object.keys(networkStats[id][subKey])
                    .sort()
                    .map((attr) =>
                      attr.startsWith('_') ? null : (
                        <NetworkSubCategory
                          key={attr}
                          indent={'xsmall'}
                          name={attr}
                          reformat={false}
                          number={networkStats[id][subKey][attr]}
                        />
                      )
                    )}
              </Box>
            )
          )}
      </Box>
    </Box>
  )
}
const CustomNetworkCategory = ({ name, total = 0 }) => {
  return (
    <Box fill={'horizontal'}>
      <Box
        fill={'horizontal'}
        gap="medium"
        justify="between"
        align={'start'}
        flex="grow"
        direction={'row'}
      >
        <Text weight="bold" color={'mesh-accent-dark'}>
          {name}
        </Text>
        <NumberBubble number={total} color={'mesh-green-3'} />
      </Box>
    </Box>
  )
}
const NetworkStats = ({ networkStats, total = 0, selected = '', toggleAction }) => {
  const setValue = (e) => {
    toggleAction && toggleAction(e.target)
  }
  const getButton = (option, { checked, hover }) => (
    <Box
      fill={'horizontal'}
      direction={'row'}
      align={'start'}
      justify={'start'}
      round={'small'}
      pad={'xsmall'}
      background={hover ? 'light-2' : 'none'}
      width={{ min: 'medium-small' }}
      gap={'small'}
    >
      <MeshPin color={hover ? 'focus' : checked ? 'mesh-accent-red' : 'light-6'} />
      {option.label}
    </Box>
  )
  return (
    <Box flex={false} gap={'xxsmall'}>
      <RadioButtonGroup
        name={'options'}
        value={selected}
        onChange={setValue}
        gap={'xsmall'}
        width={{ min: 'small' }}
        options={[
          {
            disabled: false,
            id: 'all',
            label: <CustomNetworkCategory name={'All'} total={total} />,
            value: ''
          }
        ].concat(
          Object.keys(networkStats)
            .filter((key) => key.startsWith('_') === false)
            .sort()
            .map((key) => {
              return {
                disabled: networkStats[key] ? networkStats[key]['_indicies'].length === 0 : false,
                id: key,
                label: <NetworkCategory key={key} id={key} networkStats={networkStats} />,
                value: key
              }
            })
        )}
      >
        {getButton}
      </RadioButtonGroup>
    </Box>
  )
}

export const AccountPin = ({ color, account = {}, show = false, text, $hover }) => {
  const { data = {}, sf_type } = account

  return (
    <div style={markerStyle}>
      <Box
        round
        // background={$hover || show ? colorByType[data.record_type] : undefined}
        elevation={show ? 'large' : 'none'}
      >
        <Stack>
          <Box fill>
            <Box
              round
              // border={$hover || (show && { color: colorByType[data.record_type], size: 'small' })}
              height={'48px'}
              width={'48px'}
              background={{
                color: $hover || show ? 'mesh-accent-red' : color,
                opacity: $hover || show ? 'strong' : 'weak'
              }}
            />
          </Box>
          <Box fill align={'center'} justify={'center'}>
            <MeshPin color={$hover || show ? 'white' : color} />
          </Box>
        </Stack>
      </Box>
    </div>
  )
}
const NetworkLegend = ({ children, ...rest }) => {
  return (
    <Box flex={false} gap={'small'} {...rest}>
      {children.map((item) => (
        <Box key={item.name} direction={'row'} gap={'small'}>
          <MeshPin color={item.color} />
          <Text weight={'bold'} color={'dark-3'}>
            {item.name}
          </Text>
        </Box>
      ))}
    </Box>
  )
}
class _Map extends Component {
  static contextType = AppContext
  
  static defaultProps = {
    fromApi: { results: [], apiParams: { with_attributes: [] } },
    apiActions: {},
    // centerOfMap: { lat: 50.404243, lng: -118.694654 },
    zoom: 11
  }
  switchTo = { collapsed: 'full', full: 'collapsed' }
  switchToIcon = {
    collapsed: <FormDown color={'brand'} />,
    full: <FormUp color={'brand'} />
  }
  state = {
    clickedIndex: undefined,
    results: [],
    toggleAttributes: '',
    panel: 'collapsed',
    networkStats: {},
    impactMetrics: [],
    metricsLoading: false
  }
  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.fromApi.results !== this.props.fromApi.results) {
      this.setAttributes({ value: '' })
      this.getMetrics()
    }
  }
  componentDidMount() {
    console.log(this.props)
    firebase.analytics().logEvent('page_view', {
      // page_location: url,
      // page_path: path,
      page_title: this.props.pageTitle
    })
    this.setAttributes({ value: '' })
    this.getMetrics()
  }
  onChildClick = (key, childProps) => {
    const intKey = parseInt(key)
    this.setState({ clickedIndex: isNaN(intKey) ? undefined : intKey })
  }
  // for react google map
  // _onChildMouseEnter = (key /*, childProps */) => {
  //   this.props.onHoverKeyChange(key);
  // }
  //
  // _onChildMouseLeave = (/* key, childProps */) => {
  //   this.props.onHoverKeyChange(null);
  // }
  getMetrics = () => {
    this.setState({ metricsLoading: true })
    const common = {
      in_area: this.props.region,
      page_size: 9999,
      annotate_metrics: true
    }
    const mkt = opsAPI.getFromMarketplace('network/impact', common)

    const retail = opsAPI.getFromRetail('network/donation_totals', common)

    const responses = Promise.all([mkt, retail])
    responses
      .then((result) => {
        const mktApiValues = getTotalByKeyFromResults(result[0].data.results, [
          'meals_provided__sum',
          'money_saved__sum',
          'food_rescued_kg__sum',
          'co2_emissions_kg__sum'
        ])
        const retailApiValues = getTotalByKeyFromResults(result[1].data.results, [
          'meals_provided__sum',
          'money_saved__sum',
          'food_rescued_kg__sum',
          'co2_emissions_kg__sum'
        ])
        const combinedValues = {
          meals_provided__sum:
            mktApiValues.meals_provided__sum + retailApiValues.meals_provided__sum,
          money_saved__sum: mktApiValues.money_saved__sum + retailApiValues.money_saved__sum,
          food_rescued_kg__sum:
            mktApiValues.food_rescued_kg__sum + retailApiValues.food_rescued_kg__sum,
          co2_emissions_kg__sum:
            mktApiValues.co2_emissions_kg__sum + retailApiValues.co2_emissions_kg__sum
        }
        const metricsObj = calculateImpactMetrics(
          0,
          0,
          {
            showMoneySaved: true
          },
          combinedValues
        )
        this.setState({ impactMetrics: metricsObj, metricsLoading: false })
      })
      .catch((err) => {
        this.setState({
          metricsLoading: false
        })
        this.context.actions.showResponseAsError(err)
      })
  }
  prepNetworkStats = (results) => {
    const attrPathMapping = {
      Charity: 'recipients.donationRecipients',
      Caterer: 'foodSuppliers.foodService',
      Composter: 'serviceProviders.composters',
      Consumer: 'recipients.z_other',
      Distributor: 'foodSuppliers.distributors',
      // Foodservice: "foodSuppliers",
      'Farmer (Livestock)': 'recipients.donationRecipients',
      'Food Service': 'recipients.buyers',
      Grower: 'foodSuppliers.growers',
      Liquidator: ['serviceProviders.liquidators', 'recipients.buyers'],
      Manufacturer: 'foodSuppliers.manufacturers',
      Nonprofit: 'recipients.donationRecipients',
      Processor: ['foodSuppliers.processors', 'recipients.buyers'],
      'Retail/Grocer': ['foodSuppliers.retailGrocers', 'recipients.buyers'],
      'Restaurant/Hotel': ['foodSuppliers.foodServices', 'recipients.buyers'],
      Supplier: 'foodSuppliers.z_other',
      Wholesale: 'recipients.buyers',
      Waste: 'serviceProviders.waste',
      Other: 'serviceProviders.z_other'
    }
    // Grower
    //
    // Manufacturer
    //
    // Processor
    //
    // Distributor
    //
    // Retail/grocer
    const statsByGroup = {
      foodSuppliers: { _total: 0, _indicies: [] },
      serviceProviders: { _total: 0, _indicies: [] },
      recipients: { _total: 0, _indicies: [] },
      _other: { _total: 0, _indicies: [] },
      _noAttrs: { _total: 0, _indicies: [] }
    }
    const stats = {
      Caterer: 0,
      Charity: 0,
      Composter: 0,
      Consumer: 0,
      Distributor: 0,
      'Farmer (Livestock)': 0,
      'Food Service': 0,
      Grower: 0,
      Liquidator: 0,
      Manufacturer: 0,
      Nonprofit: 0,
      Processor: 0,
      'Restaurant/Hotel': 0,
      'Retail/Grocer': 0,
      Supplier: 0
    }
    const organizeBy = Object.keys(stats)
    //   ("Caterer", "Caterer"),
    //   ("Charity", "Charity"),
    // ("Composter", "Composter"),
    //   ("Consumer", "Consumer"),
    //   ("Distributor", "Distributor"),
    //   ("Farmer (Livestock)", "Farmer (Livestock)"),
    //   ("FBBC Member", "FBBC Member"),
    //   ("Food Service", "Food Service"),
    //   ("Grower", "Grower"),
    //   ("Liquidator", "Liquidator"),
    //   ("Manufacturer", "Manufacturer"),
    //   ("Marketplace", "Marketplace"),
    //   ("Nonprofit", "Nonprofit"),
    //   ("Other", "Other"),
    //   ("Processor", "Processor"),
    //   ("Restaurant/Hotel", "Restaurant/Hotel"),
    //   ("Retail/Grocer", "Retail/Grocer"),
    //   ("Retail Program", "Retail Program"),
    //   ("Supplier", "Supplier"),
    //   ("UWLM 'Love Hub'", "UWLM 'Love Hub'"),
    //   ("Waste", "Waste"),
    //   ("Wholesale", "Wholesale"),
    results.forEach((item, index) => {
      if (item.sf_attributes) {
        // get sf account attributes
        const attrs = Object.keys(item.sf_attributes)
        if (attrs.length) {
          let count = 0
          attrs.map((sfAttribute) => {
            // see if this attribute is mapped to a categories
            if (attrPathMapping[sfAttribute]) {
              count += 1
              let groups = []
              // find matching groups for the attribute
              if (typeof attrPathMapping[sfAttribute] === 'string') {
                groups.push(attrPathMapping[sfAttribute])
              } else {
                groups = attrPathMapping[sfAttribute]
              }
              groups.forEach((grp) => {
                const splitPath = grp.split('.')
                const mainCategory = splitPath[0]
                let subCategory = splitPath[1]
                if (subCategory === undefined) {
                  subCategory = '_hidden'
                }
                // special rules apply for Buyers - must also have the Marketplace attribute
                if (
                  mainCategory === 'recipients' &&
                  subCategory === 'buyers' &&
                  item.sf_attributes['Marketplace'] === undefined
                ) {
                  return
                }

                // create any missing sub categories first
                if (statsByGroup[mainCategory][subCategory] === undefined) {
                  statsByGroup[mainCategory][subCategory] = { _total: 0, _indicies: [], _path: grp }
                }
                if (statsByGroup[mainCategory][subCategory][sfAttribute] === undefined) {
                  statsByGroup[mainCategory][subCategory][sfAttribute] = 0
                }

                // populate stats to display later - we will count this up all at once so we have access
                // to multiple counts at different levels
                statsByGroup[mainCategory][subCategory][sfAttribute] += 1
                statsByGroup[mainCategory][subCategory]['_total'] += 1
                statsByGroup[mainCategory]['_total'] += 1

                // track the indicies, we will need these to know which to toggle as layers and also
                // use for unique counts to display
                if (statsByGroup[mainCategory]['_indicies'].indexOf(index) === -1) {
                  statsByGroup[mainCategory]['_indicies'].push(index)
                }
                if (
                  subCategory !== '_hidden' &&
                  statsByGroup[mainCategory][subCategory]['_indicies'].indexOf(index) === -1
                ) {
                  statsByGroup[mainCategory][subCategory]['_indicies'].push(index)
                }
              })
            }

            if (organizeBy.includes(sfAttribute)) {
              stats[sfAttribute] += 1
            }
          })
          // if the given attribute has no mapping place it under 'Other' category
          if (count === 0) {
            statsByGroup._other['_total'] += 1
            statsByGroup._other['_indicies'].push(index)
          }
        } else {
          // todo: there is a chance an account has no public attributes to show,
          //  in this case we should clean these up - for internal use - remove later
          statsByGroup._noAttrs['_total'] += 1
          statsByGroup._noAttrs['_indicies'].push(index)
        }
      }
    })
    this.setState({ networkStats: statsByGroup })
  }
  setAttributes = ({ value = '' }) => {
    this.prepArray(this.props.fromApi.results, value)
    this.prepNetworkStats(this.props.fromApi.results)
  }
  togglePanel = () => {
    const currentSize = this.state.panel
    this.setState({ panel: this.switchTo[currentSize] })
  }
  prepArray = (data = [], filterKey = '') => {
    const { networkStats } = this.state
    const allowedIndicies = networkStats[filterKey] ? networkStats[filterKey]['_indicies'] : []
    const results = allowedIndicies.length
      ? data.filter((o, index) => allowedIndicies.includes(index))
      : data
    // const results = filters.length
    //   ? data.filter((o) => filters.some((v) => Object.keys(o.sf_attributes).includes(v)))
    //   : data

    this.setState({ results: results, toggleAttributes: filterKey, clickedIndex: undefined })
  }
  getAccountColor = (index, useAttributes = false) => {
    const account = this.state.results[index] || {}
    if (useAttributes) {
      const attributes = Object.keys(account.sf_attributes || {})
      let color = 'mesh-accent-red'

      if (attributes.length) {
        attributes.forEach((attr) => {
          if (colorByType[attr]) {
            color = colorByType[attr]
          }
        })
      }
      return color
    }
    return account.sf_type ? colorByType[account.sf_type] : 'mesh-accent-red'
  }
  render() {
    const panelSizes = { collapsed: '58px', full: '100%' }
    const {
      clickedIndex,
      results,
      toggleAttributes,
      panel,
      networkStats,
      metricsLoading,
      impactMetrics
    } = this.state
    const sideDockWidth = '360px'
    return (
      <Box fill direction={'row'}>
        <PageCard
          show={true}
          width={{ min: sideDockWidth, max: sideDockWidth }}
          headerProps={{ height: 'xxsmall' }}
          // headerProps={{ background: 'mesh-green-1' }}
          background={{
            color: 'light-1'
          }}
          border={{ side: 'right', color: 'light-6', size: 'xsmall' }}
          initial={{
            opacity: 0
            // height: panelSizes.collapsed,
            // minHeight: panelSizes.collapsed
          }}
          animate={{
            opacity: 1
            // height: panelSizes[panel] || panelSizes.collapsed
          }}
          fill={false}
          flex={false}
          exit={{}}
          // iconButtons={[
          //   {
          //     icon: this.switchToIcon[panel],
          //     // color: 'mesh-green-1',
          //     onClick: this.togglePanel
          //   }
          // ]}
          elevation="large"
          overflow={'hidden'}
        >
          <Main gap={'small'} pad={{ left: 'medium', top: 'medium', horizontal: 'small' }}>
            <Heading level={3} color="dark-3" margin={{ bottom: 'none', top: 'none' }}>
              Impact Metrics
            </Heading>
            <Text size="small" color="dark-6" margin={{ bottom: 'small', top: 'none' }}>
              {
                this.props.helpText
              }
            </Text>
            <DonationMetricsSmall
              rows={'auto'}
              columns={{
                count: 'fill',
                size: '40%'
              }}
              loading={metricsLoading}
              flex={false}
              height={{ min: '254px' }}
              metrics={impactMetrics}
            />
            <MetricsBox
              width={'100%'}
              item={{
                Icon: Group,
                id: 'network',
                value: this.props.fromApi.results.length,
                name: 'Total network participants'
                // unit: 'participants'
              }}
              loading={metricsLoading}
            />
            <Box fill={'horizontal'} pad="xsmall" flex={false}>
              <Anchor
                size={'small'}
                label={'Our stats explained'}
                reverse
                weight={'normal'}
                color={'dark-6'}
                icon={<CircleInformation size={'small'} />}
                href={'https://foodmesh.ca/our-stats-explained/'}
                target={'_blank'}
              />
            </Box>
            <Box />
            <Box
              height={'4px'}
              fill={'horizontal'}
              border={{ side: 'top', color: 'light-3', size: 'medium' }}
            />
            <Box />{' '}
            <Heading level={3} color="dark-3" margin={{ bottom: 'none', top: 'small' }}>
              Network Participants
            </Heading>
            <Text color="dark-6" weight={'bold'} margin={{ bottom: 'small', top: 'none' }}>
              Showing {results.length} / {this.props.fromApi.results.length} participant(s)
            </Text>
            <Box pad={{ left: 'small' }} flex={false} gap={'medium'}>
              <NetworkStats
                total={this.props.fromApi.results.length}
                networkStats={networkStats}
                selected={toggleAttributes}
                toggleAction={this.setAttributes}
              />
            </Box>
            <Box />{' '}
            <Text textAlign="center" color={'dark-6'} size="xsmall">
              © Copyright Mesh Exchange Inc.
            </Text>
            <Box pad={'medium'} />
          </Main>
        </PageCard>
        <Box height={'100%'} width={'100%'}>
          <GoogleMap
            bootstrapURLKeys={{ key: process.env.REACT_APP_GCP_API_KEY }}
            defaultCenter={this.props.centerOfMap}
            defaultZoom={this.props.zoom}
            onChildClick={this.onChildClick}
          // onChildMouseEnter={this._onChildMouseEnter}
          // onChildMouseLeave={this._onChildMouseLeave}
          >
            {results.map(
              (account, index) =>
                account.location && (
                  <AccountPin
                    key={index}
                    color={this.getAccountColor(index)}
                    show={clickedIndex === index}
                    account={account}
                    lat={account.location.coordinates[1]}
                    lng={account.location.coordinates[0]}
                    text={String(index)}
                  />
                )
            )}
          </GoogleMap>
          <MotionLayer
            margin={{ left: sideDockWidth, top: '62px' }}
            modal={false}
            position={'top-left'}
            plain
          >
            <Box
              margin={{ left: 'small', top: 'small' }}
              background="white"
              round="small"
              elevation="medium"
              pad={{ horizontal: 'medium', vertical: 'small' }}
              direction={'row'}
              gap={'small'}
              align={'center'}
              justify={'center'}
            >
              {/*<Text color="dark-6" weight={'bold'} margin={{ bottom: 'small', top: 'none' }}>*/}
              {/*  Map legend*/}
              {/*</Text>*/}
              <NetworkLegend direction={'row'}>
                {[
                  { name: 'Charity / Nonprofit', color: colorByType.Charity },
                  { name: 'Retailer', color: colorByType.Store },
                  { name: 'Business', color: colorByType['Corporate Customer'] }
                ]}
              </NetworkLegend>
            </Box>
          </MotionLayer>
          <MotionLayer
            margin={{ right: '60px', bottom: '20px' }}
            modal={false}
            position={'bottom-right'}
            plain
          >
            <AnimatePresence>
              <Box gap={'small'} pad={'medium'}>
                {/*{this.props.apiFilterMenu}*/}
                <AccountCard
                  headerProps={{
                    background: this.getAccountColor(clickedIndex)
                  }}
                  initial={{ opacity: 1, y: 50, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 300 }}
                  exit={{ opacity: 0, y: 50, height: 0 }}
                  overflow={'hidden'}
                  key={'info'}
                  flex={'grow'}
                  width={'medium-small'}
                  round={'small'}
                  fill={false}
                  closeAction={this.onChildClick}
                  {...(results[clickedIndex] || {})}
                  show={clickedIndex > -1}
                />
              </Box>
            </AnimatePresence>
          </MotionLayer>
        </Box>
      </Box>
    )
  }
}
export const ApiMap = withOpsApi(_Map)
export const NetworkMap = ({ values }) => {
  return (
    <ApiMap
      region={values.region}
      centerOfMap={values.centerOfMap}
      pageTitle={values.pageTitle}
      helpText={values.helpText}
      apiParams={{ in_area: [values.region], limit: 999 }}
      // apiFilters={[
      //   {
      //     label: 'Type',
      //     field: 'with_attributes',
      //     options: memberTypes,
      //     optionProps: { multiple: true },
      //     defaultValue: memberTypes
      //   }
      // ]}
      apiPath={'retail/network/'}
    />
  )
}
